/**
 * API client de configuração global de quiz.
 *
 * Integração com /v1/Configuration/quiz-config:
 * - Leitura: GET
 * - Gravação: POST com o body de configuração
 *
 * Observação: o backend pode retornar `text/plain` com JSON serializado,
 * então o parse de string é feito de forma defensiva.
 */

/** Resposta esperada do GET /v1/Configuration/quiz-config */
export interface QuizConfigResponse {
  minScore: number;
  maxAttempts: number | null;
  timeLimitSeconds: number | null;
  shuffleQuestions: boolean;
}

/**
 * Body enviado no POST de gravação /v1/Configuration/quiz-config
 *
 * Regras:
 * - shuffleQuestions deve sempre ser true
 * - timeLimitSeconds pode ser null (fase2)
 * - maxAttempts: null quando unlimited
 */
export interface QuizConfigUpdateRequest {
  minScore: number;
  maxAttempts: number | null;
  timeLimitSeconds: null;
  shuffleQuestions: true;
}

/** Cliente mínimo necessário para as chamadas (get/post). Compatível com ApiClient do core. */
export interface QuizConfigApiClient {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown): Promise<T>;
}

const QUIZ_CONFIG_PATH = 'v1/Configuration/quiz-config';

/** Corpo ausente (ex.: HTTP 204 No Content na resposta): sem config persistida na API. */
function isEmptyQuizConfigResponse(raw: unknown): boolean {
  if (raw === undefined || raw === null) return true;
  if (raw === '') return true;
  if (typeof raw === 'string' && raw.trim() === '') return true;
  return false;
}

/** Valores padrão quando não há JSON (204 / corpo vazio). Alinhado ao estado inicial da UI. */
function emptyQuizConfigDefaults(): QuizConfigResponse {
  return {
    minScore: 0,
    maxAttempts: null,
    timeLimitSeconds: null,
    shuffleQuestions: true,
  };
}

function parseQuizConfigRaw(raw: unknown): QuizConfigResponse {
  const asAny = raw as unknown;

  const obj =
    typeof asAny === 'string'
      ? (() => {
          try {
            return JSON.parse(asAny) as unknown;
          } catch {
            return null;
          }
        })()
      : asAny;

  if (!obj || typeof obj !== 'object') {
    throw new Error('Invalid quiz-config response');
  }

  const record = obj as Record<string, unknown>;

  const minScore =
    typeof record.minScore === 'number'
      ? record.minScore
      : typeof record.minScore === 'string'
        ? Number(record.minScore)
        : NaN;

  const rawMaxAttempts = record.maxAttempts;
  let maxAttempts: number | null;
  if (rawMaxAttempts === null || rawMaxAttempts === undefined) {
    maxAttempts = null;
  } else if (typeof rawMaxAttempts === 'number') {
    maxAttempts = rawMaxAttempts;
  } else if (typeof rawMaxAttempts === 'string') {
    const trimmed = rawMaxAttempts.trim();
    maxAttempts = trimmed.toLowerCase() === 'null' ? null : Number(trimmed);
  } else {
    maxAttempts = null;
  }

  const rawTimeLimit = record.timeLimitSeconds;
  let timeLimitSeconds: number | null;
  if (rawTimeLimit === null || rawTimeLimit === undefined) {
    timeLimitSeconds = null;
  } else if (typeof rawTimeLimit === 'number') {
    timeLimitSeconds = rawTimeLimit;
  } else if (typeof rawTimeLimit === 'string') {
    const trimmed = rawTimeLimit.trim();
    timeLimitSeconds = trimmed.toLowerCase() === 'null' ? null : Number(trimmed);
  } else {
    timeLimitSeconds = null;
  }

  const shuffleQuestions =
    typeof record.shuffleQuestions === 'boolean'
      ? record.shuffleQuestions
      : Boolean(record.shuffleQuestions);

  if (!Number.isFinite(minScore)) {
    throw new Error('Invalid quiz-config minScore');
  }

  if (maxAttempts !== null && !Number.isFinite(maxAttempts)) {
    maxAttempts = null;
  }

  return {
    minScore,
    maxAttempts,
    timeLimitSeconds,
    shuffleQuestions,
  };
}

/**
 * Cria o cliente de API de quiz-config.
 * Recebe o api do app (b2b/b2b2c) e retorna { fetchQuizConfig, updateQuizConfig }.
 */
export function createQuizConfigApi(api: QuizConfigApiClient) {
  return {
    /**
     * Busca a configuração global atual (GET /v1/Configuration/quiz-config).
     * Retorna QuizConfigResponse normalizado.
     */
    async fetchQuizConfig(): Promise<QuizConfigResponse> {
      const url = `/${QUIZ_CONFIG_PATH}`;
      const raw = await api.get<unknown>(url);
      if (isEmptyQuizConfigResponse(raw)) {
        return emptyQuizConfigDefaults();
      }
      return parseQuizConfigRaw(raw);
    },

    /**
     * Salva a configuração global atual (POST /v1/Configuration/quiz-config).
     */
    async updateQuizConfig(
      body: QuizConfigUpdateRequest
    ): Promise<void> {
      const url = `/${QUIZ_CONFIG_PATH}`;
      await api.post<unknown>(url, body);
    },
  };
}

export type QuizConfigApi = ReturnType<typeof createQuizConfigApi>;

